import React, { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Lock, Save, RotateCcw, Languages } from "lucide-react";
import { SLIDE_LABELS, FieldList, flattenFields, setPath, collectChanges } from "./editorFields";

const API = process.env.REACT_APP_BACKEND_URL;

const PasswordGate = ({ onUnlocked }) => {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const unlock = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/presentation/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        sessionStorage.setItem("pres_editor_pw", password);
        onUnlocked();
      } else {
        toast.error("Senha incorreta.");
      }
    } catch {
      toast.error("Erro de conexão. Tente novamente.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-6 py-10 flex flex-col gap-4 max-w-sm">
      <div className="flex items-center gap-2 text-[#184A45] text-sm font-semibold">
        <Lock size={16} /> Digite a senha do editor
      </div>
      <Input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && unlock()}
        placeholder="Senha"
        data-testid="editor-password-input"
        className="bg-white/70 border-[#D8D1C6]"
      />
      <Button onClick={unlock} disabled={busy || !password} data-testid="editor-password-submit" className="bg-[#184A45] hover:bg-[#003F3A] text-[#FCF9F0] w-fit">
        Entrar
      </Button>
    </div>
  );
};

const LangTabs = ({ editLang, setEditLang }) => (
  <div className="flex items-center gap-1 border border-[#D8D1C6] rounded-full p-1 bg-white/60 w-fit">
    {["pt", "en"].map((l) => (
      <button
        key={l}
        onClick={() => setEditLang(l)}
        data-testid={`editor-lang-${l}`}
        className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest transition-colors ${
          editLang === l ? "bg-[#184A45] text-[#FCF9F0]" : "text-[#8a7f6d] hover:text-[#184A45]"
        }`}
      >
        {l === "pt" ? "Português" : "English"}
      </button>
    ))}
  </div>
);

export default function Editor({ open, onOpenChange, content, onSaved }) {
  const [unlocked, setUnlocked] = useState(() => Boolean(sessionStorage.getItem("pres_editor_pw")));
  const [editLang, setEditLang] = useState("pt");
  const [translate, setTranslate] = useState(true);
  const [draft, setDraft] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && content) {
      setDraft(structuredClone(content));
      setBaseline(structuredClone(content));
    }
  }, [open, content]);

  const changedPaths = useMemo(() => {
    if (!draft || !baseline) return [];
    const mine = collectChanges(baseline[editLang], draft[editLang]);
    const other = editLang === "pt" ? "en" : "pt";
    const theirs = new Set(collectChanges(baseline[other], draft[other]));
    return mine.filter((p) => !theirs.has(p));
  }, [draft, baseline, editLang]);

  const save = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/presentation/content`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          password: sessionStorage.getItem("pres_editor_pw") || "",
          content: draft,
          translate,
          edited_lang: editLang,
          changed_paths: changedPaths,
        }),
      });
      if (res.status === 401) {
        sessionStorage.removeItem("pres_editor_pw");
        setUnlocked(false);
        return;
      }
      if (!res.ok) throw new Error("save failed");
      const data = await res.json();
      onSaved(data.content);
      setDraft(structuredClone(data.content));
      setBaseline(structuredClone(data.content));
      if (data.translate_error) {
        toast.warning("Textos salvos, mas a tradução automática falhou. Revise o outro idioma.");
      } else {
        toast.success(data.translated ? "Textos salvos e traduzidos para o outro idioma." : "Textos salvos.");
      }
    } catch {
      toast.error("Não foi possível salvar. Tente novamente.");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Restaurar todos os textos para o padrão original?")) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/presentation/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: sessionStorage.getItem("pres_editor_pw") || "" }),
      });
      if (!res.ok) throw new Error("reset failed");
      onSaved(null);
      toast.success("Textos restaurados para o padrão.");
      onOpenChange(false);
    } catch {
      toast.error("Não foi possível restaurar.");
    } finally {
      setBusy(false);
    }
  };

  const slideKeys = draft ? Object.keys(draft[editLang]).filter((k) => k.startsWith("s")) : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[600px] bg-[#F4EFE5] border-l border-[#D8D1C6] overflow-y-auto p-0" data-testid="editor-panel">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#D8D1C6] sticky top-0 bg-[#F4EFE5] z-10">
          <SheetTitle className="text-[#184A45] font-bold uppercase tracking-wide">Editar textos da apresentação</SheetTitle>
          <SheetDescription className="text-[#8a7f6d] text-xs">
            As alterações valem para todos que abrirem a apresentação.
          </SheetDescription>
        </SheetHeader>

        {!unlocked && <PasswordGate onUnlocked={() => setUnlocked(true)} />}

        {unlocked && draft && (
          <div className="px-6 py-5 space-y-5">
            <LangTabs editLang={editLang} setEditLang={setEditLang} />

            <label className="flex items-start gap-3 px-4 py-3 cursor-pointer bg-white/50 border border-[#D8D1C6] rounded-lg">
              <Switch checked={translate} onCheckedChange={setTranslate} data-testid="editor-translate-switch" className="mt-0.5 data-[state=checked]:bg-[#184A45]" />
              <span className="text-xs text-[#4a463f] leading-relaxed">
                <span className="font-semibold text-[#184A45] flex items-center gap-1"><Languages size={13} /> Traduzir automaticamente</span>
                Ao salvar, as alterações feitas em {editLang === "pt" ? "português" : "inglês"} serão traduzidas por IA e aplicadas também em {editLang === "pt" ? "inglês" : "português"}.
              </span>
            </label>

            <Accordion type="single" collapsible className="space-y-2">
              {slideKeys.map((sk) => (
                <AccordionItem key={sk} value={sk} className="border border-[#D8D1C6] rounded-lg bg-white/50 px-4">
                  <AccordionTrigger className="text-sm font-semibold text-[#184A45] hover:no-underline py-3" data-testid={`editor-slide-${sk}`}>
                    {SLIDE_LABELS[sk] || sk}
                  </AccordionTrigger>
                  <AccordionContent className="space-y-3 pb-4">
                    <FieldList
                      fields={flattenFields(draft[editLang][sk], sk)}
                      onEdit={(path, value) =>
                        setDraft((prev) => {
                          const next = structuredClone(prev);
                          setPath(next[editLang], path, value);
                          return next;
                        })
                      }
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="sticky bottom-0 bg-[#F4EFE5] border-t border-[#D8D1C6] -mx-6 px-6 py-4 flex items-center justify-between gap-3">
              <Button variant="outline" onClick={reset} disabled={busy} data-testid="editor-reset" className="border-[#D8D1C6] text-[#8a7f6d] hover:text-[#184A45] bg-transparent">
                <RotateCcw size={14} className="mr-1" /> Restaurar padrão
              </Button>
              <Button onClick={save} disabled={busy} data-testid="editor-save" className="bg-[#184A45] hover:bg-[#003F3A] text-[#FCF9F0]">
                <Save size={14} className="mr-1" /> {busy ? "Salvando..." : "Salvar alterações"}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
